import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/context/I18n";
import { ArrowRight, Headphones, Radio, ListMusic, Sparkles, Ban, Smartphone } from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const authed = user && user !== false;

  const features = [
    { icon: Ban, t: "f_adfree_t", d: "f_adfree_d" },
    { icon: Headphones, t: "f_audio_t", d: "f_audio_d" },
    { icon: Radio, t: "f_radio_t", d: "f_radio_d" },
    { icon: ListMusic, t: "f_lists_t", d: "f_lists_d" },
    { icon: Smartphone, t: "f_pwa_t", d: "f_pwa_d" },
    { icon: Sparkles, t: "f_invite_t", d: "f_invite_d" },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#050505]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/icon.jpg" alt="SONLYO" className="w-8 h-8 rounded-lg" />
            <span className="font-display text-xl font-black tracking-tighter">SONLYO</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {authed ? (
              <Link data-testid="landing-app-btn" to="/app" className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-5 py-2 text-sm font-semibold transition-colors">{t("open_app")}</Link>
            ) : (
              <>
                <Link data-testid="landing-login-btn" to="/login" className="text-sm text-zinc-300 hover:text-white px-4 py-2">{t("login")}</Link>
                <Link data-testid="landing-register-btn" to="/register" className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-5 py-2 text-sm font-semibold transition-colors">{t("have_invite")}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(5,5,5,0.6),#050505), url(https://images.pexels.com/photos/7605370/pexels-photo-7605370.jpeg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="max-w-6xl mx-auto px-6 pt-28 pb-32 relative z-10">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#FF2A4D] border border-[#FF2A4D]/30 rounded-full px-3 py-1 mb-6">
            <Sparkles className="w-3 h-3" /> {t("hero_badge")}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-3xl">
            {t("hero_title_a")} <span className="text-[#FF2A4D]">{t("hero_title_b")}</span>
          </h1>
          <p className="text-zinc-300 text-lg mt-6 max-w-xl leading-relaxed">{t("hero_sub")}</p>
          <div className="mt-9 flex items-center gap-4">
            <Link data-testid="hero-cta-btn" to={authed ? "/app" : "/register"}
              className="group bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-7 py-3 font-semibold flex items-center gap-2 transition-all hover:-translate-y-0.5">
              {authed ? t("hero_cta_open") : t("hero_cta_in")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            {!authed && <Link to="/login" className="text-zinc-300 hover:text-white font-medium">{t("have_account")}</Link>}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f) => (
            <div key={f.t} className="bg-[#121212] border border-white/5 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:bg-[#1c1c1c]">
              <div className="w-11 h-11 rounded-lg bg-[#FF2A4D]/10 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[#FF2A4D]" />
              </div>
              <h3 className="font-display text-lg font-bold tracking-tight">{t(f.t)}</h3>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{t(f.d)}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-zinc-600">SONLYO · sonlyo.com · {new Date().getFullYear()}</footer>
    </div>
  );
}
